import { useBrowserLocation } from "wouter/use-browser-location";
import { useSyncExternalStore } from "react";
import { LAYER_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { isFlatShell } from "@/lib/shellFlat";

/**
 * SaleFish Admin as a LAYER over the page, not a page of its own — PropPay's
 * Platform Settings (Andrew, 2026-09-27).
 *
 * From 768px wide up (`LAYER_QUERY`: any iPad, a desk), every `/admin/*`
 * route opens in `PlatformModal`, over whatever page the admin was on. The URL
 * is still the real admin route — `/admin/users`, `/admin/billing/quotes/:id`
 * — so every admin screen's own links, deep links, reload and Back work
 * exactly as they did when these were pages. What changes is what renders
 * UNDERNEATH: the app shell is wrapped in a wouter `<Router>` whose location
 * hook is `useAppLocation` below, and while an admin route is showing that hook
 * answers with the last NON-admin location instead. The page beneath keeps its
 * route, its params, its breadcrumb and its project context, and never
 * remounts; closing the modal is just navigating back to it.
 *
 * Company Settings (`/company-admin/*`) is the same kind of aside and takes the
 * same treatment, in `CompanyModal` (Andrew, 2026-09-28): both areas are
 * "layer paths", and the page underneath survives either.
 *
 * Below 768px (a phone) there is no room for a modal round it, so both areas
 * stay pages and this hook is a pass-through. This is a width, deliberately
 * NOT the navigation split: an upright iPad navigates by the tab bar
 * (2026-10-31) yet still opens these as a layer, and turning it with the
 * modal up must not swap the modal for a page.
 */

const ADMIN = /^\/admin(\/|$)/;
export const isAdminPath = (path: string) => ADMIN.test(path);

const COMPANY = /^\/company-admin(\/|$)/;
export const isCompanyPath = (path: string) => COMPANY.test(path);

/** A path that opens as a layer over the page from the sidenav breakpoint up. */
export const isLayerPath = (path: string) => isAdminPath(path) || isCompanyPath(path);

/** Admin routes that are not a menu item of their own, by the section they live in. */
const SECTION_OF: Record<string, string> = {
  "/admin/translations": "/admin/settings",
};

/** Which Platform Settings section a location is in, from the rail's own items. */
export function adminSectionFor<T extends { href: string }>(location: string, sections: T[]): T | undefined {
  const path = location.split("?")[0];
  const owner = Object.entries(SECTION_OF).find(([p]) => path === p || path.startsWith(p + "/"))?.[1];
  const target = owner ?? path;
  return sections.find((s) => target === s.href || target.startsWith(s.href + "/"));
}

/**
 * Company Settings paths that are not a section of their own, by the section
 * they belong to. `/company-admin/details` is Company Details as the phone
 * addresses it (`/company-admin` is its section list there); the Stripe
 * billing screens sit under Features & Billing, as the tab bar already treats
 * them.
 */
const COMPANY_SECTION_OF: Record<string, string> = {
  "/company-admin/details": "/company-admin",
  "/company-admin/billing": "/company-admin/features",
};

/**
 * Which Company Settings section a location is in. Exact on the href, not a
 * prefix: Company Details lives at `/company-admin`, which every other section
 * starts with.
 */
export function companySectionFor<T extends { href: string }>(location: string, sections: T[]): T | undefined {
  const path = location.split("?")[0].replace(/\/$/, "") || "/";
  const owner = Object.entries(COMPANY_SECTION_OF).find(([p]) => path === p || path.startsWith(p + "/"))?.[1];
  const target = owner ?? path;
  return sections.find((s) => target === s.href);
}

/** Where a direct load of a layered URL puts the page underneath. */
const FALLBACK = "/projects";

let lastPath: string | null = null;
let lastSearch = "";

/** The page underneath the modal, as `path?search`. */
export function appLocationHref(): string {
  if (!lastPath) return FALLBACK;
  return lastSearch ? `${lastPath}?${lastSearch}` : lastPath;
}

/* In the flat shell (lib/shellFlat) the settings areas are SPACES, not a
   layer: their pages sit in the raised page like any other, their sections
   are the menu's rows. Read once: the shell does not change under a page. */
const FLAT = typeof window !== "undefined" && isFlatShell();
const layeredNow = () =>
  !FLAT && typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(LAYER_QUERY).matches;

/** Whether a layer path is shown as a modal right now. */
export const usePlatformLayer = () => useMediaQuery(LAYER_QUERY) && !FLAT;
/** Whether the settings areas are drawn a phone's way: their sections as a list, then one at a time.
 *  Asked as "narrower than the layer width", not "not the layer width", so a
 *  render with no screen to measure (the server, a test) draws the desk's way
 *  as it always has, rather than a phone's back link that needs a session. */
export const useSettingsStacked = () => useMediaQuery("(max-width: 767.98px)");

const subscribeSearch = (cb: () => void) => {
  // wouter patches pushState/replaceState to emit these; popstate covers Back.
  const events = ["popstate", "pushState", "replaceState", "hashchange"];
  events.forEach((e) => addEventListener(e, cb));
  return () => events.forEach((e) => removeEventListener(e, cb));
};
const currentSearch = () => (typeof location !== "undefined" ? location.search.replace(/^\?/, "") : "");

/**
 * The location hook for the app shell's `<Router>`. Remembers every location
 * that is not a layer path, and while a layer is up answers a layer location
 * with the remembered one. Navigation is never intercepted — `navigate` is the
 * browser's.
 */
export function useAppLocation(opts?: { ssrPath?: string }) {
  const [path, navigate] = useBrowserLocation(opts);
  const layered = useMediaQuery(LAYER_QUERY) && !FLAT;
  if (layered && isLayerPath(path)) {
    return [lastPath ?? FALLBACK, navigate] as ReturnType<typeof useBrowserLocation>;
  }
  if (!isLayerPath(path)) {
    lastPath = path;
    lastSearch = currentSearch();
  }
  return [path, navigate] as ReturnType<typeof useBrowserLocation>;
}

/** The search half of the same rule, so `useSearch()` beneath agrees with the path. */
export function useAppSearch() {
  const search = useSyncExternalStore(subscribeSearch, currentSearch, () => "");
  const path = typeof location !== "undefined" ? location.pathname : "";
  if (!isLayerPath(path)) {
    lastSearch = search;
    return search;
  }
  return layeredNow() ? lastSearch : search;
}
